import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Style helpers for node decisions
const getNodeCardBorderStyle = (decision) => {
  const styles = {
    admit: 'border-emerald-500/30',
    normalize: 'border-amber-500/30',
    reject: 'border-red-500/30'
  };
  return styles[decision] || 'border-zinc-700';
};

// Confidence text style helper
const getConfidenceTextStyle = (confidence) => {
  const styles = {
    high: 'text-emerald-400',
    medium: 'text-amber-400'
  };
  return styles[confidence] || 'text-red-400';
};

// Trust class badge
const TrustBadge = ({ trustClass }) => {
  const colors = {
    anchor: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    governed: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    external_compliant: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
    external_weak: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    untrusted: 'bg-red-500/20 text-red-400 border-red-500/30',
    coordinator: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  };
  return (
    <span className={`px-2 py-0.5 rounded border text-xs font-medium ${colors[trustClass] || 'bg-zinc-700 text-zinc-400'}`}>
      {trustClass}
    </span>
  );
};

// Decision badge
const DecisionBadge = ({ decision }) => {
  const colors = {
    admit: 'bg-emerald-500/20 text-emerald-400',
    normalize: 'bg-amber-500/20 text-amber-400',
    quarantine: 'bg-orange-500/20 text-orange-400',
    reject: 'bg-red-500/20 text-red-400',
  };
  return (
    <span className={`px-2 py-1 rounded text-xs font-medium ${colors[decision] || 'bg-zinc-700 text-zinc-400'}`}>
      {decision?.toUpperCase()}
    </span>
  );
};

// Outcome badge
const OutcomeBadge = ({ outcome, size = 'md' }) => {
  const colors = {
    approve: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    hold: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    escalate: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    insufficient_evidence: 'bg-red-500/20 text-red-400 border-red-500/30',
  };
  const sizes = {
    sm: 'px-2 py-1 text-xs',
    md: 'px-3 py-1.5 text-sm',
    lg: 'px-4 py-2 text-base font-bold',
  };
  return (
    <span className={`rounded border ${colors[outcome] || 'bg-zinc-700 text-zinc-400'} ${sizes[size]}`}>
      {outcome?.toUpperCase().replace('_', ' ')}
    </span>
  );
};

// Metric row
const MetricRow = ({ label, pass }) => (
  <div className="flex items-center justify-between py-1">
    <span className="text-zinc-400 text-sm">{label}</span>
    <span className={`text-sm font-medium ${pass ? 'text-emerald-400' : 'text-red-400'}`}>
      {pass ? 'PASS' : 'FAIL'}
    </span>
  </div>
);

// Node decision card
const NodeDecisionCard = ({ node }) => {
  const [expanded, setExpanded] = useState(false);
  
  return (
    <div className={`border rounded-lg overflow-hidden ${getNodeCardBorderStyle(node.decision)}`}>
      <div 
        className="p-3 bg-zinc-800/50 cursor-pointer hover:bg-zinc-800/70"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-2 h-2 rounded-full ${
              node.is_live ? 'bg-emerald-400 animate-pulse' : 'bg-zinc-500'
            }`} />
            <div>
              <div className="font-medium text-zinc-200">{node.node_id}</div>
              <div className="text-xs text-zinc-500">{node.role}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <TrustBadge trustClass={node.trust_class} />
            <DecisionBadge decision={node.decision} />
            <span className="text-zinc-600 text-xs">
              {node.is_live ? 'LIVE' : 'SIM'}
            </span>
            <span className="text-zinc-500">{expanded ? '▲' : '▼'}</span>
          </div>
        </div>
      </div>
      
      {expanded && (
        <div className="p-4 bg-zinc-900 border-t border-zinc-800 space-y-3 text-sm">
          <div>
            <span className="text-zinc-500">Type:</span>{' '}
            <span className="text-zinc-300">{node.node_type}</span>
          </div>
          <div>
            <span className="text-zinc-500">Contribution:</span>
            <div className="mt-1 p-2 bg-zinc-950 rounded text-zinc-400 text-xs">
              {node.contribution_summary}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <span className="text-zinc-500">Provenance:</span>{' '}
              <span className={node.provenance_result.startsWith('SUCCESS') ? 'text-emerald-400' : 'text-red-400'}>
                {node.provenance_result}
              </span>
            </div>
            <div>
              <span className="text-zinc-500">Weight:</span>{' '}
              <span className="text-zinc-300">{node.weight}</span>
            </div>
          </div>
          {node.quarantine_spans?.length > 0 && (
            <div>
              <span className="text-zinc-500">Quarantined:</span>
              <div className="mt-1 p-2 bg-red-500/10 rounded text-red-400 text-xs font-mono">
                {node.quarantine_spans.join(', ')}
              </div>
            </div>
          )}
          {node.tools_allowed?.length > 0 && (
            <div>
              <span className="text-zinc-500">Tools Allowed:</span>{' '}
              {node.tools_allowed.map((t) => (
                <span key={t} className="inline-block px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs rounded mr-1">
                  {t}
                </span>
              ))}
            </div>
          )}
          {node.policy_refs_used?.length > 0 && (
            <div>
              <span className="text-zinc-500">Policy Refs:</span>{' '}
              {node.policy_refs_used.map((r) => (
                <span key={r} className="inline-block px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded mr-1">
                  {r}
                </span>
              ))}
            </div>
          )}
          <div className="text-xs text-zinc-600">
            Evidence: {node.local_evidence_ref}
          </div>
        </div>
      )}
    </div>
  );
};

export default function FederationAdmin() {
  const [scenarios, setScenarios] = useState(null);
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [latestRun, setLatestRun] = useState(null);
  const [loading, setLoading] = useState({ scenarios: true, run: false });
  const [error, setError] = useState(null);
  const [useLiveKatlas, setUseLiveKatlas] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [scenariosRes, runRes] = await Promise.all([
          axios.get(`${API}/admin/federation/scenarios`),
          axios.get(`${API}/admin/federation/runs/latest`).catch(() => ({ data: null }))
        ]);
        setScenarios(scenariosRes.data);
        if (scenariosRes.data?.scenarios?.length > 0) {
          setSelectedScenario(scenariosRes.data.scenarios[0].id);
        }
        if (runRes.data) setLatestRun(runRes.data);
      } catch (e) {
        setError(e.response?.data?.detail || 'Failed to load federation data');
      } finally {
        setLoading(l => ({ ...l, scenarios: false }));
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Intentionally empty - load once on mount

  const runScenario = async () => {
    if (!selectedScenario) return;
    setLoading(l => ({ ...l, run: true }));
    setError(null);
    try {
      const res = await axios.post(`${API}/admin/federation/run`, {
        scenario_id: selectedScenario,
        use_live_katlas: useLiveKatlas
      });
      setLatestRun(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to run scenario');
    } finally {
      setLoading(l => ({ ...l, run: false }));
    }
  };

  const downloadEvidence = async (fileType) => {
    if (!latestRun?.run_id) return;
    try {
      const response = await axios.get(
        `${API}/admin/federation/evidence/${latestRun.run_id}/download/${fileType}`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const ext = fileType === 'policy_refs' ? 'md' : 'json';
      link.setAttribute('download', `${fileType}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      setError(`Failed to download ${fileType}`);
    }
  };

  if (loading.scenarios) {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center">
        <div className="text-zinc-500">Loading federation testbed...</div>
      </div>
    );
  }

  const selectedScenarioData = scenarios?.scenarios?.find(s => s.id === selectedScenario);

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* Header */}
        <header className="border-b border-zinc-800 pb-4">
          <div className="flex items-center gap-3">
            <span className="px-2 py-1 bg-purple-500/20 text-purple-400 text-xs font-medium rounded">INTERNAL</span>
            <h1 className="text-2xl font-bold tracking-tight text-white">
              Federated Runtime Governance
            </h1>
            <span className="px-2 py-1 bg-zinc-800 text-zinc-400 text-xs font-mono rounded">v1</span>
          </div>
          <p className="text-zinc-500 text-sm mt-1">
            Mixed-trust network coordination testbed • {scenarios?.scenarios?.length || 0} scenarios
          </p>
        </header>

        {/* Error */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-400">
            <strong>Error:</strong> {error}
          </div>
        )}

        {/* Business Question */}
        <div className="bg-zinc-900/50 rounded-lg p-4 border border-zinc-800">
          <div className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Business Question</div>
          <div className="text-zinc-200 italic">"{scenarios?.business_question}"</div>
        </div>

        {/* Scenario Selector */}
        <section className="bg-zinc-900 rounded-xl border border-zinc-800 p-6">
          <h2 className="text-lg font-semibold text-zinc-200 mb-4">Scenario Selection</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            {scenarios?.scenarios?.map(s => (
              <label 
                key={s.id}
                className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                  selectedScenario === s.id 
                    ? 'border-purple-500/50 bg-purple-500/10' 
                    : 'border-zinc-700 hover:border-zinc-600'
                }`}
              >
                <input
                  type="radio"
                  name="scenario"
                  value={s.id}
                  checked={selectedScenario === s.id}
                  onChange={(e) => setSelectedScenario(e.target.value)}
                  className="sr-only"
                />
                <div className="font-medium text-zinc-200">{s.title}</div>
                <div className="text-xs text-zinc-500 mt-1">{s.description}</div>
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-xs text-zinc-600">{s.participants?.length} participants</span>
                  <span className="text-xs">→</span>
                  <OutcomeBadge outcome={s.expected_outcome} size="sm" />
                </div>
              </label>
            ))}
          </div>

          {/* Options */}
          <div className="flex items-center gap-4 mb-4 pb-4 border-b border-zinc-800">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={useLiveKatlas}
                onChange={(e) => setUseLiveKatlas(e.target.checked)}
                className="accent-emerald-500"
              />
              <span className="text-zinc-400">Use live Katlas anchor</span>
            </label>
          </div>

          {/* Run Button */}
          <button
            onClick={runScenario}
            disabled={loading.run || !selectedScenario}
            className="px-6 py-3 bg-purple-600 hover:bg-purple-500 disabled:bg-zinc-700 disabled:text-zinc-500 rounded-lg font-medium transition-colors"
            data-testid="run-scenario-btn"
          >
            {loading.run ? 'Running Scenario...' : 'Run Scenario'}
          </button>
        </section>

        {/* Results */}
        {latestRun && (
          <>
            {/* Coordination Result Banner */}
            <div className={`rounded-xl p-6 border ${
              latestRun.scoreboard.overall_pass 
                ? 'bg-emerald-500/10 border-emerald-500/30' 
                : 'bg-red-500/10 border-red-500/30'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <span className={`text-4xl ${latestRun.scoreboard.overall_pass ? 'text-emerald-400' : 'text-red-400'}`}>
                    {latestRun.scoreboard.overall_pass ? '✓' : '✗'}
                  </span>
                  <div>
                    <div className="text-sm text-zinc-400">Final Decision</div>
                    <OutcomeBadge outcome={latestRun.scoreboard.final_decision} size="lg" />
                  </div>
                  <div className="ml-4">
                    <div className="text-sm text-zinc-400">Confidence</div>
                    <div className={`text-lg font-bold ${getConfidenceTextStyle(latestRun.scoreboard.confidence_status)}`}>
                      {latestRun.scoreboard.confidence_status?.toUpperCase()}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-zinc-500">
                    Run: <span className="font-mono text-purple-400">{latestRun.run_id}</span>
                  </div>
                  <div className="text-xs text-zinc-600">{latestRun.timestamp_utc}</div>
                </div>
              </div>
            </div>

            {/* Scoreboard */}
            <section className="bg-zinc-900 rounded-xl border border-zinc-800 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-zinc-200">Federation Scoreboard</h2>
                <div className="flex gap-2">
                  <button
                    onClick={() => downloadEvidence('scoreboard')}
                    className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 rounded text-xs"
                  >
                    scoreboard.json
                  </button>
                  <button
                    onClick={() => downloadEvidence('coordination_receipt')}
                    className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 rounded text-xs"
                  >
                    coordination_receipt.json
                  </button>
                  <button
                    onClick={() => downloadEvidence('policy_refs')}
                    className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 rounded text-xs"
                  >
                    policy_refs.md
                  </button>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                <div className="bg-zinc-800/50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-zinc-100">{latestRun.scoreboard.total_participants}</div>
                  <div className="text-xs text-zinc-500 uppercase">Participants</div>
                </div>
                <div className="bg-emerald-500/10 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-emerald-400">{latestRun.scoreboard.accepted_contributors}</div>
                  <div className="text-xs text-emerald-500/70 uppercase">Accepted</div>
                </div>
                <div className="bg-red-500/10 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-red-400">{latestRun.scoreboard.excluded_contributors}</div>
                  <div className="text-xs text-red-500/70 uppercase">Excluded</div>
                </div>
                <div className={`rounded-lg p-4 text-center ${latestRun.scoreboard.katlas_anchor_used ? 'bg-emerald-500/10' : 'bg-amber-500/10'}`}>
                  <div className={`text-2xl font-bold ${latestRun.scoreboard.katlas_anchor_used ? 'text-emerald-400' : 'text-amber-400'}`}>
                    {latestRun.scoreboard.katlas_anchor_used ? 'YES' : 'NO'}
                  </div>
                  <div className="text-xs text-zinc-500 uppercase">Katlas Anchor</div>
                </div>
                <div className={`rounded-lg p-4 text-center ${latestRun.scoreboard.outcome_match ? 'bg-emerald-500/10' : 'bg-amber-500/10'}`}>
                  <div className={`text-2xl font-bold ${latestRun.scoreboard.outcome_match ? 'text-emerald-400' : 'text-amber-400'}`}>
                    {latestRun.scoreboard.outcome_match ? '✓' : '~'}
                  </div>
                  <div className="text-xs text-zinc-500 uppercase">Expected Match</div>
                </div>
              </div>

              {/* Metrics */}
              <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-wider mb-3">Network Metrics</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-zinc-800/30 rounded-lg p-4">
                {latestRun.scoreboard.metrics && Object.entries(latestRun.scoreboard.metrics).map(([key, value]) => (
                  <MetricRow key={key} label={key.replace(/_/g, ' ')} pass={value} />
                ))}
              </div>
            </section>

            {/* Node Decisions */}
            <section className="space-y-3">
              <h2 className="text-lg font-semibold text-zinc-200">Node Decisions</h2>
              {latestRun.node_decisions?.map((node) => (
                <NodeDecisionCard key={node.node_id} node={node} />
              ))}
            </section>

            {/* Coordination Receipt Summary */}
            <section className="bg-zinc-900 rounded-xl border border-zinc-800 p-6">
              <h2 className="text-lg font-semibold text-zinc-200 mb-4">Coordination Receipt</h2>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-zinc-500">Receipt ID:</span>{' '}
                  <span className="font-mono text-purple-400">{latestRun.coordination_receipt?.receipt_id}</span>
                </div>
                <div>
                  <span className="text-zinc-500">Signature:</span>{' '}
                  <span className="font-mono text-zinc-400 text-xs">{latestRun.coordination_receipt?.coordination_signature}</span>
                </div>
              </div>
              
              <div className="mt-4 grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-zinc-500 uppercase mb-2">Accepted Contributors</div>
                  <div className="space-y-1">
                    {latestRun.coordination_receipt?.accepted_contributors?.map((c) => (
                      <div key={c} className="text-sm text-emerald-400">✓ {c}</div>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-zinc-500 uppercase mb-2">Excluded Contributors</div>
                  <div className="space-y-1">
                    {latestRun.coordination_receipt?.excluded_contributors?.map((c) => (
                      <div key={c.node_id} className="text-sm text-red-400">✗ {c.node_id}: {c.reason}</div>
                    ))}
                    {(!latestRun.coordination_receipt?.excluded_contributors || latestRun.coordination_receipt.excluded_contributors.length === 0) && (
                      <div className="text-sm text-zinc-500">None</div>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-4">
                <div className="text-xs text-zinc-500 uppercase mb-2">Policy References Applied</div>
                <div className="flex gap-2">
                  {latestRun.coordination_receipt?.policy_refs?.map((r) => (
                    <span key={r} className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded">{r}</span>
                  ))}
                </div>
              </div>
            </section>
          </>
        )}

        {/* Footer */}
        <footer className="text-center text-zinc-600 text-xs pt-4 border-t border-zinc-800">
          Federated Runtime Governance v1 · Internal Testbed · Mixed-Trust Network Coordination
        </footer>
      </div>
    </div>
  );
}
